import { useEffect, useState } from "react";
import { toast } from "sonner";
import { addDays, addMonths } from "date-fns";
import { Loader2, CalendarCheck2, CalendarRange } from "lucide-react";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogBody as DialogBody,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { KindIcon } from "@/components/Badges";
import { formatDate } from "@/lib/format";
import { KIND_LABEL, toISO, fromISO } from "@/lib/calendar";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const QUICK = {
  plan: [
    { label: "I morgon", fn: (t) => addDays(t, 1) },
    { label: "+1 veke", fn: (t) => addDays(t, 7) },
    { label: "+2 veker", fn: (t) => addDays(t, 14) },
    { label: "Ved fristen", fn: (t, due) => (due ? fromISO(due) : t) },
  ],
  move: [
    { label: "+1 mnd", fn: (t, due) => addMonths(due ? fromISO(due) : t, 1) },
    { label: "+3 mnd", fn: (t, due) => addMonths(due ? fromISO(due) : t, 3) },
    { label: "+6 mnd", fn: (t, due) => addMonths(due ? fromISO(due) : t, 6) },
    { label: "+12 mnd", fn: (t, due) => addMonths(due ? fromISO(due) : t, 12) },
  ],
};

/**
 * mode = "plan": set planlagt utføringsdato (fristen står).
 * mode = "move": flytt sjølve fristen (blir logga).
 * Desktop: dialog. Mobil: bottom drawer.
 */
export const DeadlineActionDialog = ({ open, onOpenChange, event, mode = "plan", today, onDone }) => {
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && event) {
      setDate(mode === "plan" ? event.planned_date || "" : event.date || "");
      setReason(mode === "plan" ? event.planned_note || "" : "");
    }
  }, [open, event, mode]);

  if (!event) return null;
  const isPlan = mode === "plan";
  const todayDate = today ? fromISO(today) : new Date();

  const submit = async () => {
    if (!date) return toast.error("Vel ein dato.");
    setBusy(true);
    try {
      await api.patch(`/machines/${event.machine_id}/deadlines`, {
        kind: event.kind,
        action: isPlan ? "plan" : "move",
        date,
        reason: reason.trim() || null,
      });
      toast.success(isPlan ? `Planlagt ${formatDate(date)} · ${event.label}` : `Frist flytta til ${formatDate(date)} · ${event.label}`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikkje lagre. Prøv igjen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`deadline-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            {isPlan ? <CalendarCheck2 className="h-5 w-5 text-primary" /> : <CalendarRange className="h-5 w-5 text-primary" />}
            {isPlan ? "Planlegg utføring" : "Flytt frist"}
          </DialogTitle>
          <DialogDescription>
            {isPlan
              ? "Vel dagen du planlegg å utføre jobben. Fristen står uendra, og planen blir fjerna automatisk når rapporten er lagra."
              : "Endrar sjølve fristen. Endringa blir logga med namn og grunn."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="flex items-center gap-3 rounded-xl border bg-muted/40 p-3">
            <KindIcon kind={event.kind} />
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{event.label}</div>
              <div className="text-xs text-muted-foreground">
                {KIND_LABEL[event.kind]} · {event.customer_name || "—"} · frist {formatDate(event.date)}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold">{isPlan ? "Planlagt dato" : "Ny frist"}</Label>
            <DatePicker
              value={date}
              onChange={setDate}
              clearable={false}
              testid="deadline-dialog-date"
              title={isPlan ? "Planlagt dato" : "Ny frist"}
              presets={[]}
            />
            <div className="flex flex-wrap gap-1.5 pt-1">
              {QUICK[mode].map((q) => {
                const iso = toISO(q.fn(todayDate, event.date));
                const active = iso === date;
                return (
                  <button
                    key={q.label}
                    type="button"
                    onClick={() => setDate(iso)}
                    aria-pressed={active}
                    className={cn(
                      "h-10 rounded-full border bg-card px-3.5 text-xs font-semibold transition-colors hover:bg-accent md:h-8 md:px-3",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
                      active && "border-transparent bg-[#0B0D10] text-white hover:bg-[#0B0D10] dark:bg-white dark:text-[#0B0D10]"
                    )}
                    data-testid={`deadline-quick-${q.label.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
                  >
                    {q.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold">{isPlan ? "Notat (valfritt)" : "Grunn (valfritt)"}</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isPlan ? "T.d. «Tek saman med Komatsu hjå same kunde»" : "T.d. «Kunden har utsett oppstart»"}
              rows={2}
              className="resize-none"
              data-testid="deadline-dialog-reason"
            />
          </div>
        </DialogBody>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="deadline-dialog-cancel">Avbryt</Button>
          <Button onClick={submit} disabled={busy || !date} data-testid="deadline-dialog-submit">
            {busy ? <Loader2 className="animate-spin" /> : isPlan ? <CalendarCheck2 /> : <CalendarRange />}
            {isPlan ? "Lagre plan" : "Flytt frist"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
